import { Alert, Box, Chip, Paper, Typography } from "@mui/material";
import DOMPurify from "dompurify";

export interface CampaignCauseStage {
  id: number;
  position: number;
  title: string;
  target_amount: string | number;
}

export interface CampaignCauseData {
  title: string;
  description: string;
  currency: string;
  category_name?: string;
  category?: { name: string } | null;
  fundraiser_type: string;
  goal_amount: string | null;
  end_date?: string | null;
  beneficiary_type?: string;
  beneficiary_name?: string | null;
  cover_image_url?: string | null;
  images?: { id: number; image_url: string }[];
  stages?: CampaignCauseStage[];
  deceased_data?: {
    first_name: string;
    surname: string;
    date_of_birth?: string | null;
    date_of_death?: string | null;
    relationship_to_deceased?: string | null;
  } | null;
}

const money = (amount: string | number | null | undefined, currency: string) => {
  if (amount === null || amount === undefined || amount === "") return "—";
  return `${currency === "ZWL" ? "ZWL " : "$"}${Number(amount).toLocaleString()}`;
};

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const BENEFICIARY_LABELS: Record<string, string> = {
  self: "Themselves",
  other: "Someone else",
  charity: "A charity",
};

const Fact = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value || "—"}
    </Typography>
  </Box>
);

/** What the fundraiser is actually for -- the story, who benefits, the goal and
 * the pictures -- so an admin can judge the cause before approving it. */
const CampaignCauseCard = ({ campaign }: { campaign: CampaignCauseData }) => {
  const category = campaign.category?.name ?? campaign.category_name;
  const images = [
    ...(campaign.cover_image_url ? [campaign.cover_image_url] : []),
    ...(campaign.images ?? [])
      .map((i) => i.image_url)
      .filter((url) => url !== campaign.cover_image_url),
  ];
  const description = (campaign.description ?? "").trim();
  const d = campaign.deceased_data;

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
      <Typography fontWeight={700} sx={{ mb: 2 }}>
        Fundraiser cause
      </Typography>

      {images.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Box
            component="img"
            src={images[0]}
            alt=""
            sx={{
              width: "100%",
              maxHeight: 360,
              objectFit: "cover",
              borderRadius: 2,
            }}
          />
          {images.length > 1 && (
            <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
              {images.slice(1).map((url) => (
                <Box
                  key={url}
                  component="img"
                  src={url}
                  alt=""
                  sx={{ width: 88, height: 88, objectFit: "cover", borderRadius: 1.5 }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        {category && <Chip size="small" label={category} />}
        <Chip
          size="small"
          variant="outlined"
          label={campaign.fundraiser_type === "ongoing" ? "Ongoing (no cap)" : "Goal-based"}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 2.5,
        }}
      >
        <Fact
          label="Goal"
          value={
            campaign.fundraiser_type === "ongoing"
              ? "No cap"
              : money(campaign.goal_amount, campaign.currency)
          }
        />
        <Fact label="End date" value={formatDate(campaign.end_date)} />
        <Fact
          label="Raising for"
          value={BENEFICIARY_LABELS[campaign.beneficiary_type ?? ""] ?? campaign.beneficiary_type ?? ""}
        />
        <Fact label="Beneficiary" value={campaign.beneficiary_name ?? ""} />
      </Box>

      {d && (
        <Alert severity="info" icon={false} sx={{ mb: 2.5, borderRadius: 2 }}>
          In memory of{" "}
          <strong>
            {d.first_name} {d.surname}
          </strong>
          {d.relationship_to_deceased ? ` (${d.relationship_to_deceased})` : ""}
          {d.date_of_birth || d.date_of_death
            ? ` · ${formatDate(d.date_of_birth)} – ${formatDate(d.date_of_death)}`
            : ""}
        </Alert>
      )}

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {campaign.title}
      </Typography>
      {description ? (
        <Box
          sx={{
            fontSize: 14,
            lineHeight: 1.7,
            color: "text.primary",
            "& p": { m: 0, mb: 1.5 },
            "& img": { maxWidth: "100%", borderRadius: 1 },
            overflowWrap: "anywhere",
          }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
        />
      ) : (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          The organiser hasn&apos;t written a story for this fundraiser.
        </Alert>
      )}

      {!!campaign.stages?.length && (
        <Box sx={{ mt: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Stages
          </Typography>
          {campaign.stages.map((stage) => (
            <Box
              key={stage.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.75,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2">
                {stage.position}. {stage.title}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {money(stage.target_amount, campaign.currency)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default CampaignCauseCard;
